/**
 * SOLID Review
 * S — Strong. Sole job is catching render errors below it and showing a fallback (either the
 *     caller-supplied `fallback` prop or a built-in "Something went wrong" panel with a reset button). 75
 *     lines, one concern.
 * O — Good. A caller extends behavior purely via the `fallback` prop, without editing this class.
 * L — Strong. This IS the one inheritance point in the batch: it extends React's `Component<Props, State>`
 *     and correctly implements `getDerivedStateFromError` / `componentDidCatch` per the React contract
 *     (the only mechanism React offers for error boundaries — no class alternative exists).
 * I — Strong. Two optional/required props (`children`, `fallback`), both directly used.
 * D — Strong. Depends only on `react`; no application/infrastructure imports.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error, info);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      /* c8 ignore next -- error?.message always defined when hasError=true; '??' fallback is unreachable */
      const errorMessage = this.state.error?.message ?? 'An unexpected error occurred.';
      return (
        this.props.fallback ?? (
          <div
            role="alert"
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#0a0a0a',
              color: '#ef4444',
              fontFamily: 'system-ui, sans-serif',
              padding: 24,
              gap: 12,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 20 }}>Something went wrong</h2>
            <p style={{ margin: 0, fontSize: 14, color: '#9ca3af', maxWidth: 400, textAlign: 'center' }}>
              {errorMessage}
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              style={{
                marginTop: 8,
                padding: '8px 20px',
                borderRadius: 6,
                border: '1px solid #374151',
                backgroundColor: '#1f2937',
                color: '#f9fafb',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              Try again
            </button>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
